import axios from 'axios';
import { FC, useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { HLSMedia } from '../../helpers/hls-media';
import { WebRTCMedia } from '../../helpers/webrtc-media';

const useResumeMediaOnVisibilityChange = (videoEl: HTMLVideoElement) => {
    // Handle page visibility change - keep stream running
    useEffect(() => {
        const handleVisibilityChange = () => {
            const videoElement = videoEl;

            if (!document.hidden && videoElement) {
                // Page is visible again - ensure playback continues
                if (videoElement.paused) {
                    videoElement.play().catch((err) => {
                        console.error('Error resuming playback:', err);
                    });
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [videoEl]);
};

interface CctvPlayerProps {
    hlsUrl: string;
    webrtcUrl?: string;
    className?: string;
    onData?: () => void;
    onError?: () => void;
}

const CctvPlayer: FC<CctvPlayerProps> = (props) => {
    const { hlsUrl, webrtcUrl, className, onData, onError } = props;

    const videoRef = useRef<HTMLVideoElement>(null);
    const webRTCMediaRef = useRef<WebRTCMedia>(null);
    const hlsMediaRef = useRef<HLSMedia>(null);
    const isOnlineRef = useRef<boolean>(null);
    const onDataRef = useRef(onData);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onDataRef.current = onData;
    }, [onData]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    const initHlS = useCallback(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        hlsMediaRef.current = new HLSMedia({ src: hlsUrl });

        hlsMediaRef.current.init(videoElement);

        hlsMediaRef.current.on(HLSMedia.Events.DATA, () => {
            onDataRef.current?.();
            isOnlineRef.current = true;
        });

        hlsMediaRef.current.on(HLSMedia.Events.ERROR, () => {
            onErrorRef.current?.();
            isOnlineRef.current = false;
        });
    }, [hlsUrl]);

    const destroyHls = useCallback(() => {
        hlsMediaRef.current?.destroy();
        hlsMediaRef.current = null;
    }, []);

    const destroyWebRTC = useCallback(() => {
        webRTCMediaRef.current?.destroy();
        webRTCMediaRef.current = null;
    }, []);

    const initWebRTC = useCallback(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        webRTCMediaRef.current = new WebRTCMedia({ src: webrtcUrl });

        webRTCMediaRef.current.init(videoElement);

        webRTCMediaRef.current.on(WebRTCMedia.Events.DATA, () => {
            onDataRef.current?.();
            isOnlineRef.current = true;
        });

        webRTCMediaRef.current.on(WebRTCMedia.Events.ERROR, () => {
            isOnlineRef.current = false;
            destroyWebRTC();
            initHlS();
        });
    }, [webrtcUrl, destroyWebRTC, initHlS]);

    const initMedia = useCallback(async () => {
        try {
            isOnlineRef.current = null;

            await axios.get(hlsUrl);

            destroyWebRTC();
            destroyHls();

            if (webrtcUrl) {
                initWebRTC();
            } else {
                initHlS();
            }
        } catch {
            onErrorRef.current?.();
            isOnlineRef.current = false;
            destroyWebRTC();

            destroyHls();
        }
    }, [hlsUrl, webrtcUrl, initWebRTC, initHlS, destroyWebRTC, destroyHls]);

    useEffect(() => {
        initMedia();

        return () => {
            destroyWebRTC();
            destroyHls();
        };
    }, [destroyWebRTC, destroyHls, initMedia]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            // Only check if page is visible
            if (!document.hidden && isOnlineRef.current === false) {
                initMedia();
            }
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, [initMedia]);

    useResumeMediaOnVisibilityChange(videoRef.current);

    return (
        <video
            className={twMerge('w-full h-full object-fill', className)}
            ref={videoRef}
            muted
            autoPlay
            playsInline
        />
    );
};

export default CctvPlayer;
